export default customElements.define('w-textbox-v2', class TextboxV2 extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-textbox-v2';
        const config = {
            styleSelector: styleSelector,
            templateHTML: `
            <div class="${styleSelector}__wrapper" data-root>
                <div class="${styleSelector}__controls-container" data-controlscontainer>
                    <div class="${styleSelector}__input-container">
                        <span class="${styleSelector}__prefix" data-prefix hidden></span>
                        <input type="text" class="${styleSelector}__input" autocomplete="off" data-input />
                        <span class="${styleSelector}__suffix" data-suffix></span>
                    </div>
                    <span data-slot="button" data-button></span>
                </div>
                <w-icon class="${styleSelector}__icon" data-icon></w-icon>
                <span class="u-display__hidden" aria-live="polite" data-state></span>
            </div>`,
            cacheDom: [ 'data-controlscontainer', 'data-input', 'data-button', 'data-prefix', 'data-suffix', 'data-icon', 'data-state' ]
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);
            this.value = this.dom.input.value;
            this.valid = this.dom.input.checkValidity();
            this.hasWarning = false;
            this.previousValidity = null;
            this.dom.input.getWrappingComponent = () => this;

            this._setupButton();

            this.dom.input.addEventListener('blur', this._eventBindCallbacks.blur);
            this.dom.root.addEventListener('click', () => {
                this.dom.input.focus();
            });
            this.dom.input.addEventListener('change', this._eventBindCallbacks.keyup);
            this.dom.input.addEventListener('keyup', this._eventBindCallbacks.keyup);

            this.dom.input.addEventListener('wheel', event => {
                if(this.dom.input === document.activeElement) {
                    event.preventDefault();
                }
            });

            this.addEventListener('invalid', (event) => event.preventDefault(), true);
        };
    }

    static get validTypes () {
        return [
            'date',
            'decimal',
            'email',
            'integer',
            'password',
            'text',
            'age',
            'phone',
            'postcode',
            'address'
        ];
    }

    static inputTypeMap = {
        'date': 'date',
        'decimal': 'number',
        'email': 'text',
        'integer': 'number',
        'password': 'password',
        'text': 'text',
        'age': 'number',
        'phone': 'number',
        'postcode': 'text',
        'address': 'text'
    }

    static get validWarnings () {
        return [
            'max',
            'min',
            'required'
        ];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('autocompletion', {default: ''}),
            new wealthify.Prop('decimalplaces', {
                default: 2,
                type: wealthify.propTypes.Number()
            }),
            new wealthify.Prop('key', {default: wealthify.type.number.getRandom(10)}),
            new wealthify.Prop('max'),
            new wealthify.Prop('min'),
            new wealthify.Prop('maxlength'),
            new wealthify.Prop('minlength'),
            new wealthify.Prop('numberfield', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('placeholderprop'),
            new wealthify.Prop('pattern'),
            new wealthify.Prop('prefix'),
            new wealthify.Prop('readonlyprop', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('required', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('suffix'),
            new wealthify.Prop('type', {
                default: 'text',
                type: wealthify.propTypes.ArrayItem(this.validTypes)
            }),
            new wealthify.Prop('validoverride', {
                nullable: true,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('value', {default: ''}),
            new wealthify.Prop('warnings', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.IsInArray(this.validWarnings)]
            }),
            wealthify.props.DarkMode(),
            wealthify.props.Disabled(),
        ];
    }

    get propCallbacks () {
        return {
            autocompletion: () => {
                const hasValue = this.autocompletion !== '';
                const isList = hasValue && this.autocompletion.indexOf('#') !== -1;

                if (!hasValue) {
                    this.dom.input.removeAttribute('list');
                    this.dom.input.setAttribute('autocomplete', 'off');

                    return;
                }

                if (isList) {
                    const list = this.autocompletion.split('#')[1];

                    this.dom.input.setAttribute('list', list);
                } else {
                    this.dom.input.setAttribute('autocomplete', this.autocompletion);
                }
            },
            decimalplaces: (value) => {
                if (value && this.constructor.inputTypeMap[this.type] == 'number') {
                    this.dom.input.setAttribute('step', '.'.padEnd(value, 0) + '1');
                } else {
                    this.dom.input.removeAttribute('step');
                }
            },

            disabledprop: (value) => {
                const classListAction = value ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}--disabled`);
                this.dom.input.disabled = value;
            },

            key: () => {
                this.dom.input.id = this.key;
                this.dom.input.name = this.key;
            },

            max: () => this._minMaxUpdate(this.max, 'max'),

            min: () => this._minMaxUpdate(this.min, 'min'),

            maxlength: () => this._minMaxUpdate(this.maxlength, 'maxlength'),

            minlength: () => this._minMaxUpdate(this.minlength, 'minlength'),

            numberfield: (value) => {
                const classListAction = value ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}__wrapper--number-field`);
            },

            pattern: () => {
                this.dom.input.pattern = this.pattern;
            },

            placeholderprop: (value) => {
                this.dom.input.placeholder = value;
            },

            prefix: (value) => {
                if (value) {
                    this.dom.prefix.removeAttribute('hidden');
                    this.dom.prefix.innerHTML = value;
                }
            },


            readonlyprop: (value) => {
                const classListAction = value ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}--readonly`);
                this.dom.controlscontainer.classList[classListAction](`${this.styleSelector}__controls-container--readonly`);
                this.dom.input.readOnly = value;
            },

            required: (value) => {
                const isErrorAWarning = this._checkIsErrorAWarning('required');

                if (isErrorAWarning) {
                    return;
                }

                this.dom.input.required = value;
                
                this._setValidity();
            },

            suffix: (value) => {
                if (value) {
                    this.dom.suffix.removeAttribute('hidden');
                    this.dom.suffix.innerHTML = value;
                }
            },

            type: (value) => {
                this.constructor.validTypes.forEach((type) => this.dom.root.classList.remove(`${this.styleSelector}--type-${type}`));
                this.dom.root.classList.add(`${this.styleSelector}--type-${value}`);

                if (this.type === 'date') {
                    this.decimalplaces = 0;
                }

                if (this.type === 'email') {
                    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]{2,10}$/.toString();

                    this.pattern = emailRegex.substring(1, emailRegex.length - 1);
                    this.max = 254;
                }

                if (value === 'decimal') {
                    this.dom.input.placeholder = 0;
                    this.dom.input.type = 'number';
                    this.dom.input.setAttribute('inputmode', 'decimal');

                    return;
                }

                if (value === 'integer' || value === 'age') {
                    this.dom.input.placeholder = this.placeholderprop || 0;
                    this.dom.input.type = 'number';
                    this.dom.input.setAttribute('inputmode', 'numeric');
                    this.decimalplaces = 0;

                    return;
                }

                if (value === 'phone') {
                    const phoneRegex = /^\+?([()\-\s]*\d[()\-\s]*){10,11}$/.toString();

                    this.pattern = phoneRegex.substring(1, phoneRegex.length - 1);
                }

                if (value === 'postcode') {
                    const postcode = /^(GIR ?0AA|(?:[a-pre-uwyzA-PR-UWYZ](?:\d|\d{2}|[a-hk-yA-HK-Y]\d|[a-hk-yA-HK-Y]\d\d|\d[a-hjkstuwA-HJKSTUW]|[a-hk-yA-HK-Y]\d[abehmnprv-yABEHMNPRV-Y])) ?\d[abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2})$/.toString();

                    this.pattern = postcode.substring(1, postcode.length - 1);
                }

                if (this.type === 'date') {
                    this.decimalplaces = 0;
                }

                if(this.type === 'address') {
                    // eslint-disable-next-line no-useless-escape
                    const addressRegex = /[^!=$>%?*^+;~<@_\|]+/.toString();
                    
                    this.pattern = addressRegex.substring(1, addressRegex.length - 1);
                }

                this.dom.input.type = this.constructor.inputTypeMap[value];
            },

            value: () => {
                if (this.value !== this.dom.input.value) {
                    this.dom.input.value = this.value;
                    this._updateAndStoreValues();
                    this._fireStateRefresh();
                }
            },

            validoverride: () => {
                if (this.validoverride === null) {
                    this.dom.input.setCustomValidity('');

                    this._updateStateFromValueChange();
                    this._validOverride = this.validoverride;

                    return;
                }

                const previousValidity = this._validOverride === undefined ? this.valid : this._validOverride;
                const customValidity = this.validoverride ? '' : 'Invalid field.';

                this._updateStateFromValidOverride(previousValidity);

                this._setValidity(this.validoverride);

                this.dom.input.setCustomValidity(customValidity);
                this._validOverride = this.validoverride;
            },

            warnings: () => {}
        };
    }

    _minMaxUpdate (value, key) {
        const isErrorAWarning = this._checkIsErrorAWarning(key);
        const isNumberType = this.type === 'integer' || this.type === 'decimal' || this.type === 'age';
        const isMinValidation = key === 'minlength';
        const isMaxValidation = key === 'maxlength';

        if (isErrorAWarning) {
            return;
        }

        if (isNumberType && (isMinValidation || isMaxValidation)) {

            if (isMinValidation) {
                this.dom.input.removeEventListener('beforeinput', this._restrictLength);
                this.dom.input.addEventListener('beforeinput', this._restrictLength);
            }

            if (isMinValidation) {
                this.removeEventListener('valuechange', this._polyfillLengthValidationOnNumberType);
                this.addEventListener('valuechange', this._polyfillLengthValidationOnNumberType);
            }
        }

        this.dom.input.setAttribute(key, value);
    }

    _restrictLength (event) {
        const value = event.currentTarget.value;
        const newValue = event.data !== null ? value + event.data : value.substring(0, value.length - 1);

        if (newValue.length > this.getWrappingComponent().maxlength) {
            event.preventDefault();
        }
    }

    _polyfillLengthValidationOnNumberType (event) {
        const validity = event.currentTarget.value.length >= this.minlength;
        const message = this.getWrappingComponent ? this.getWrappingComponent() : `Minimum length of ${this.minlength} not met`;

        this.validoverride = validity;
        this.dom.input.setCustomValidity(validity ? '' : message);
    }

    _checkIsErrorAWarning (error) {
        return this.warnings !== undefined && wealthify.type.array.includes(error, this.warnings);
    }

    _eventBindCallbacks = {
        blur: () => {
            const valid = this._isValidNotOverridden() ? this.valid : this.validoverride;

            if (valid === true && this.hasWarning === false) {
                this.dom.icon.classList.remove(`${this.styleSelector}__icon--valid`);
                this.dom.controlscontainer.classList.remove(`${this.styleSelector}__controls-container--valid`);
            }
        },

        keyup: () => {
            if (this.dom.input.value !== this.value) {
                this._restrictDecimalPlaces();
                this._fireStateRefresh();
            }
        }
    }

    _isValidNotOverridden () {
        return this.validoverride === undefined || this.validoverride === null;
    }

    _restrictDecimalPlaces () {
        if (this.type !== 'decimal' && this.type !== 'integer') {
            return;
        }

        if (this.decimalplaces === 0) {
            this.dom.input.value = this.dom.input.value.split('.')[0];

            return;
        }

        const valueDecimalPlaces = this.dom.input.value.split('.').length > 1 ? this.dom.input.value.split('.')[1].length : 0;
        const valueDecimalPlacesOffset = this.decimalplaces - valueDecimalPlaces;

        if (valueDecimalPlacesOffset < 0) {
            this.dom.input.value = this.dom.input.value.slice(0, valueDecimalPlacesOffset);
        }
    }

    _updateStateFromValueChange () {
        const previouslyNotValid = this._validOverride === false || this.previousValidity === false || this.hasWarning === true;
        const hasBeenCorrected = previouslyNotValid === true && this.valid === true;
        const shouldUpdate = hasBeenCorrected || previouslyNotValid && (this.valid === false || this.hasWarning === true);

        this.value = this.dom.input.value;

        if (this._isValidNotOverridden()) {
            this._updateAndStoreValues();
            this._updateState(shouldUpdate, this.valid);
        }
    }

    _updateStateFromValidOverride (previousValidity) {
        const previouslyNotValid = previousValidity === false;
        const hasBeenCorrected = previouslyNotValid === true && this.validoverride === true;
        const shouldUpdate = hasBeenCorrected || this.validoverride === false;

        this._updateState(shouldUpdate, this.validoverride);
    }

    _updateState (hasBeenCorrected, valid) {

        if (hasBeenCorrected || valid === false || this.hasWarning === true) {
            const classListInvalidAction = !valid ? 'add' : 'remove';
            const classListValidAction = valid && !this.hasWarning ? 'add' : 'remove';
            const classListWarningAction = this.hasWarning ? 'add' : 'remove';
            const icon = !valid || this.hasWarning ? 'danger-outlined' : 'success-outlined';

            this.dom.icon.classList[classListInvalidAction](`${this.styleSelector}__icon--invalid`);
            this.dom.icon.classList[classListValidAction](`${this.styleSelector}__icon--valid`);
            this.dom.icon.classList[classListWarningAction](`${this.styleSelector}__icon--warning`);

            this.dom.controlscontainer.classList[classListInvalidAction](`${this.styleSelector}__controls-container--invalid`);
            this.dom.controlscontainer.classList[classListValidAction](`${this.styleSelector}__controls-container--valid`);
            this.dom.controlscontainer.classList[classListWarningAction](`${this.styleSelector}__controls-container--warning`);

            this.dom.icon.type = icon;
        }

        this.dom.state.textContent = valid ? 'valid' : 'invalid';
    }

    _updateAndStoreValues () {

        this.hasWarning = false;

        if (this.warnings) {
            this._checkAndSetWarning();
        } else {
            this._setValidity();
        }
    }

    _checkAndSetWarning () {
        const minValidation = this.type === 'text' ? 'tooShort' : 'rangeUnderflow';
        const maxValidation = this.type === 'text' ? 'tooLong' : 'rangeOverflow';

        this._addWarningAttributes();

        this._setValidity();

        const errorMap = {
            [minValidation]: 'min',
            [maxValidation]: 'max',
            valueMissing: 'required',
        };

        for (let error in errorMap) {
            const currentErrors = this.dom.input.validity;
            const errorIsOverwritten = this.warnings.some((warning) => warning === errorMap[error]);

            if (currentErrors[error] && errorIsOverwritten) {
                this.hasWarning = true;
                this._setValidity(true);
            }
        }

        this._removeWarningAttributes();
    }

    _setValidity (valid) {
        const value = valid !== undefined ? valid : this.dom.input.checkValidity();

        this.dom.input.valid = value;
        this.valid = value;
        this._dispatchEvent('validchange');
    }

    _addWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.input.setAttribute(warning, this[warning]);
        });
    }

    _removeWarningAttributes () {
        this.warnings.forEach((warning) => {
            this.dom.input.removeAttribute(warning);
        });
    }

    _dispatchEvent (eventName = 'valuechange') {
        const event = document.createEvent('Event');

        event.initEvent(eventName, true, true);
        this.dispatchEvent(event);
    }

    _setupButton () {
        // TODO - Consider during slot refactor (PDB-1008)
        if (!this.dom.button || this.dom.button.tagName.toLowerCase() === 'span') {
            return;
        }

        if (this.dom.button.tagName.toLowerCase() !== 'w-widget-button') {
            return wealthify.error.throwError(this.tagname, 'The slot content for "button" in the "w-textbox" component can only be the "w-widget-button" component.');
        }

        this.dom.button.classList.add(`${this.styleSelector}__button`);
    }

    _fireStateRefresh () {
        this._updateStateFromValueChange();
        this._dispatchEvent();

        this.previousValidity = this.valid;
    }

    fireValidation () {
        this._fireStateRefresh();
    }

    removeStatuses () {
        this.dom.icon.classList.remove(`${this.styleSelector}__icon--invalid`);
        this.dom.icon.classList.remove(`${this.styleSelector}__icon--valid`);
        this.dom.icon.classList.remove(`${this.styleSelector}__icon--warning`);

        this.dom.controlscontainer.classList.remove(`${this.styleSelector}__controls-container--invalid`);
        this.dom.controlscontainer.classList.remove(`${this.styleSelector}__controls-container--valid`);
        this.dom.controlscontainer.classList.remove(`${this.styleSelector}__controls-container--warning`);

        this.dom.icon.type = 'no-icon';
    }

});